import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { colors } from "@/theme";

let _cache = null;

export function useSettings() {
  const [settings, setSettings] = useState(_cache);
  useEffect(() => {
    if (_cache) return;
    api("/settings", { auth: false }).then((s) => { _cache = s; setSettings(s); }).catch(() => {});
  }, []);
  return settings;
}

export function Footer({ isMobile }) {
  const settings = useSettings();
  const contact = settings?.contact || {};
  const social = settings?.social || {};
  const keys = settings?.policy_keys || [];
  const titles = settings?.policy_titles || {};
  const [cats, setCats] = useState([]);
  useEffect(() => { api("/categories", { auth: false }).then(setCats).catch(() => {}); }, []);

  return (
    <footer style={{ borderTop: `1px solid ${colors.border}`, background: colors.surfaceSecondary, marginTop: 56, marginBottom: isMobile ? 72 : 0 }}>
      <div className="container-max" style={{ padding: isMobile ? "40px 20px 28px" : "56px 24px 32px", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.3fr 1fr 1fr", gap: isMobile ? 32 : 48 }}>
        <div>
          <div className="font-display" style={{ fontSize: 26, fontWeight: 700 }}>Garlic</div>
          <div className="eyebrow" style={{ marginTop: 4, marginBottom: 14 }}>By Urban Nest</div>
          <p className="font-body" style={{ fontSize: 13.5, lineHeight: 1.7, color: colors.mutedText, margin: 0, maxWidth: 420 }}>
            {settings?.about_text || "Aesthetic home essentials, crockery & decor — curated for the quiet, considered home."}
          </p>
          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 9 }}>
            {contact.phone && <a href={`tel:${contact.phone}`} data-testid="footer-phone" style={contactLink}><Phone size={15} color={colors.brand} /> {contact.phone}</a>}
            {contact.email && <a href={`mailto:${contact.email}`} data-testid="footer-email" style={contactLink}><Mail size={15} color={colors.brand} /> {contact.email}</a>}
            {contact.address && <span data-testid="footer-address" style={{ ...contactLink, cursor: "default" }}><MapPin size={15} color={colors.brand} /> {contact.address}</span>}
          </div>
        </div>

        <div>
          <div className="eyebrow" style={{ marginBottom: 14 }}>Consumer Policy</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {keys.map((k) => (
              <Link key={k} to={`/policy/${k}`} data-testid={`footer-policy-${k}`} className="font-body" style={{ fontSize: 13.5, color: colors.onSurfaceSecondary, textDecoration: "none" }}>
                {titles[k]}
              </Link>
            ))}
          </div>
        </div>

        <div>
          <div className="eyebrow" style={{ marginBottom: 14 }}>Follow Us</div>
          {social.instagram && (
            <a href={social.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
              style={{ display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none", color: colors.onSurface, border: `1px solid ${colors.borderStrong}`, borderRadius: 999, padding: "9px 16px", fontSize: 13.5, fontWeight: 600 }}>
              <Instagram size={17} color={colors.accentDark} /> @garlicbyurbannest
            </a>
          )}
          <div style={{ marginTop: 18 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Shop</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Link to="/" className="font-body" style={shopLink}>All Products</Link>
              <Link to="/collections" className="font-body" style={shopLink}>Collections</Link>
              <Link to="/gifts" className="font-body" style={shopLink}>Gifts</Link>
              {cats.map((cat) => <Link key={cat.id} to={`/category/${cat.id}`} data-testid={`footer-cat-${cat.id}`} className="font-body" style={shopLink}>{cat.name}</Link>)}
            </div>
          </div>
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${colors.border}`, padding: "18px 24px", textAlign: "center" }}>
        <span className="font-body" style={{ fontSize: 12.5, color: colors.mutedText }}>© {new Date().getFullYear()} Garlic. All rights reserved.</span>
      </div>
    </footer>
  );
}

const contactLink = { display: "flex", alignItems: "center", gap: 9, fontSize: 13.5, color: colors.onSurfaceSecondary, textDecoration: "none" };
const shopLink = { fontSize: 13.5, color: colors.mutedText, textDecoration: "none" };
